import type { BookingRead } from '../api/generated/model'

/** The three questions a customer asks about their bookings. */
export type BookingTab = 'upcoming' | 'cancelled' | 'past'

export const BOOKING_TABS: BookingTab[] = ['upcoming', 'cancelled', 'past']

export const TAB_LABELS: Record<BookingTab, string> = {
  upcoming: 'Próximas',
  cancelled: 'Canceladas',
  past: 'Anteriores',
}

export const isBookingTab = (value: unknown): value is BookingTab =>
  typeof value === 'string' && (BOOKING_TABS as string[]).includes(value)

/** Statuses that mean the booking is not happening. Mirrors the backend's
 *  BAY_RELEASING_STATUSES -- these are the ones that gave the bay back. */
const RELEASED = new Set(['cancelled', 'expired'])

/**
 * Split a customer's bookings into the three tabs.
 *
 * "Previous sessions" is decided by TIME, not by a `completed` status. A
 * nightly job does now mark past sessions completed, but only nightly -- so a
 * session that finished an hour ago is still `confirmed`, and a tab keyed on
 * status would put it under Upcoming until 03:30. `ends_at` is the fact; the
 * status is extra detail to show beside it.
 *
 * `now` is a parameter so this is testable without mocking the clock.
 */
export function partitionBookings(
  bookings: BookingRead[],
  now: Date = new Date(),
): Record<BookingTab, BookingRead[]> {
  const result: Record<BookingTab, BookingRead[]> = { upcoming: [], cancelled: [], past: [] }

  for (const booking of bookings) {
    if (RELEASED.has(booking.status)) {
      result.cancelled.push(booking)
    } else if (new Date(booking.ends_at) > now) {
      result.upcoming.push(booking)
    } else {
      result.past.push(booking)
    }
  }

  // Upcoming reads best soonest-first ("what's next?"); history reads best
  // newest-first ("what just happened?"). The API returns newest-first, so only
  // upcoming needs reversing.
  result.upcoming.sort((a, b) => a.starts_at.localeCompare(b.starts_at))
  return result
}

/** Human wording for the stored reason codes. */
export const REASON_LABELS: Record<string, string> = {
  schedule: 'Surgió algo',
  wrong_time: 'Reservé el horario equivocado',
  weather: 'El clima',
  other: 'Otro motivo',
  facility_closed: 'El local cerró',
  hold_expired: 'No se completó el pago',
}

/** The reasons a customer may pick for their own booking. `facility_closed`
 *  and `hold_expired` are deliberately absent -- the API rejects them from a
 *  customer, so offering them would just produce a 403. */
export const CUSTOMER_REASONS = ['schedule', 'wrong_time', 'weather', 'other'] as const

export const STATUS_LABELS: Record<string, string> = {
  pending_payment: 'Esperando confirmación',
  confirmed: 'Confirmada',
  cancelled: 'Cancelada',
  expired: 'Vencida',
  completed: 'Completada',
  no_show: 'Ausente',
}
